/**
 * Extract plain text from Lexical JSON content
 */
export const getPlainTextFromLexical = (lexicalJson) => {
  if (!lexicalJson) return '';
  
  try {
    const parsed = typeof lexicalJson === 'string' ? JSON.parse(lexicalJson) : lexicalJson;
    if (!parsed || !parsed.root) return '';
    
    const extractText = (node) => {
      if (!node) return '';
      
      if (node.type === 'text') {
        return node.text || '';
      }
      
      if (node.children && Array.isArray(node.children)) {
        return node.children.map(extractText).join('');
      }
      
      return '';
    };
    
    return extractText(parsed.root) || '';
  } catch (e) {
    // If parsing fails, try to extract text from HTML if it's HTML
    try {
      const parser = new DOMParser();
      const dom = parser.parseFromString(lexicalJson, 'text/html');
      return dom.body.textContent || '';
    } catch (e2) {
      return lexicalJson;
    }
  }
};

/**
 * Convert Lexical JSON content to HTML
 */
export const lexicalToHTML = (lexicalJson) => {
  if (!lexicalJson) return '';
  
  try {
    const parsed = typeof lexicalJson === 'string' ? JSON.parse(lexicalJson) : lexicalJson;
    if (!parsed || !parsed.root || !parsed.root.children) return '';
    
    const convertNode = (node) => {
      if (!node) return '';
      
      switch (node.type) {
        case 'text':
          let text = node.text || '';
          // Escape HTML special characters
          text = text
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');
          
          // Handle text formatting
          if (node.format) {
            // Code formatting (16 = 0x10)
            if (node.format & 16) {
              text = `<code>${text}</code>`;
            } else {
              // Bold (1 = 0x01)
              if (node.format & 1) {
                text = `<strong>${text}</strong>`;
              }
              // Italic (2 = 0x02)
              if (node.format & 2) {
                text = `<em>${text}</em>`;
              }
              // Strikethrough (8 = 0x08)
              if (node.format & 8) {
                text = `<s>${text}</s>`;
              }
              // Underline (4 = 0x04)
              if (node.format & 4) {
                text = `<u>${text}</u>`;
              }
            }
          }
          return text;
        
        case 'heading':
          const level = node.tag ? parseInt(node.tag.replace('h', '')) : 1;
          const headingTag = `h${Math.min(Math.max(level, 1), 6)}`;
          const headingContent = node.children ? node.children.map(convertNode).join('') : '';
          return `<${headingTag}>${headingContent}</${headingTag}>`;
        
        case 'paragraph':
          const paraContent = node.children ? node.children.map(convertNode).join('') : '';
          return paraContent ? `<p>${paraContent}</p>` : '';
        
        case 'list':
          const listTag = node.listType === 'number' ? 'ol' : 'ul';
          const listContent = node.children ? node.children.map(convertNode).join('') : '';
          return `<${listTag}>${listContent}</${listTag}>`;
        
        case 'listitem':
          const itemContent = node.children ? node.children.map(convertNode).join('') : '';
          return `<li>${itemContent}</li>`;
        
        case 'quote':
          const quoteContent = node.children ? node.children.map(convertNode).join('') : '';
          return `<blockquote>${quoteContent}</blockquote>`;
        
        case 'link':
          const linkText = node.children ? node.children.map(convertNode).join('') : '';
          const url = node.url || '';
          if (linkText && url) {
            // Escape URL to prevent XSS
            const escapedUrl = url
              .replace(/&/g, '&amp;')
              .replace(/</g, '&lt;')
              .replace(/>/g, '&gt;')
              .replace(/"/g, '&quot;')
              .replace(/'/g, '&#39;');
            return `<a href="${escapedUrl}">${linkText}</a>`;
          }
          return linkText || url;
        
        case 'linebreak':
          return '<br>';
        
        default:
          // For unknown types, try to process children
          if (node.children && Array.isArray(node.children)) {
            return node.children.map(convertNode).join('');
          }
          return '';
      }
    };
    
    const html = parsed.root.children
      .map(convertNode)
      .join('')
      .trim();
    
    return html;
  } catch (e) {
    console.error('Error converting Lexical to HTML:', e);
    // Fallback: return empty string if conversion fails
    return '';
  }
};

/**
 * Convert Lexical JSON content to Markdown
 */
export const lexicalToMarkdown = (lexicalJson) => {
  if (!lexicalJson) return '';
  
  try {
    const parsed = typeof lexicalJson === 'string' ? JSON.parse(lexicalJson) : lexicalJson;
    if (!parsed || !parsed.root || !parsed.root.children) return '';
    
    const convertNode = (node) => {
      if (!node) return '';
      
      switch (node.type) {
        case 'text':
          let text = node.text || '';
          // Handle text formatting - apply in order: code, bold, italic, strikethrough, underline
          if (node.format) {
            // Code formatting (16 = 0x10)
            if (node.format & 16) {
              text = `\`${text}\``;
            } else {
              // Bold (1 = 0x01)
              if (node.format & 1) {
                text = `**${text}**`;
              }
              // Italic (2 = 0x02)
              if (node.format & 2) {
                text = `*${text}*`;
              }
              // Strikethrough (8 = 0x08)
              if (node.format & 8) {
                text = `~~${text}~~`;
              }
              // Underline (4 = 0x04) - not standard markdown, but we'll use it
              if (node.format & 4) {
                text = `__${text}__`;
              }
            }
          }
          return text;
        
        case 'heading':
          const level = node.tag ? parseInt(node.tag.replace('h', '')) : 1;
          const headingPrefix = '#'.repeat(Math.min(level, 6)) + ' ';
          const content = node.children ? node.children.map(convertNode).join('') : '';
          return headingPrefix + content + '\n\n';
        
        case 'paragraph':
          const paraContent = node.children ? node.children.map(convertNode).join('') : '';
          // Only add newlines if there's content
          return paraContent ? paraContent + '\n\n' : '';
        
        case 'list':
          const listItems = node.children ? node.children.map(convertNode).join('') : '';
          return listItems + '\n';
        
        case 'listitem':
          const listType = node.listType || 'bullet';
          const prefix = listType === 'number' ? '1. ' : '- ';
          const itemContent = node.children ? node.children.map(convertNode).join('') : '';
          // Clean up content and handle nested lists
          const cleanContent = itemContent.trim();
          return prefix + cleanContent + '\n';
        
        case 'quote':
          const quoteContent = node.children ? node.children.map(convertNode).join('') : '';
          const quoteLines = quoteContent.trim().split('\n');
          const quotedLines = quoteLines.map(line => line ? '> ' + line : '>').join('\n');
          return quotedLines + '\n\n';
        
        case 'link':
          const linkText = node.children ? node.children.map(convertNode).join('') : '';
          const url = node.url || '';
          return linkText && url ? `[${linkText}](${url})` : linkText || url;
        
        case 'linebreak':
          return '\n';
        
        default:
          // For unknown types, try to process children
          if (node.children && Array.isArray(node.children)) {
            return node.children.map(convertNode).join('');
          }
          return '';
      }
    };
    
    const markdown = parsed.root.children
      .map(convertNode)
      .join('')
      .replace(/\n{3,}/g, '\n\n') // Replace multiple newlines with double newline
      .replace(/\n\n\n/g, '\n\n') // Clean up any remaining triple newlines
      .trim();
    
    return markdown;
  } catch (e) {
    console.error('Error converting Lexical to Markdown:', e);
    // Fallback to plain text
    return getPlainTextFromLexical(lexicalJson);
  }
};
